import { DIRECT_INTEGRATION_CONNECTION_PROVIDERS } from "@frontend-shared/constants";
import { EAnalyticsEvent } from "@frontend-shared/product-analytics";
import type {
  EIntegrationProvider,
  IConnectIntegrationPayload,
} from "@frontend-shared/types";
import { EConnectorType } from "@frontend-shared/types";
import {
  getConnectionChangeQueryKeys,
  parseCallbackOutcome,
} from "@frontend-shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { integrationApi } from "@/api/services";
import { useProductAnalytics } from "@/hooks/use-product-analytics";
import i18n from "@/i18n";

// NOTE: Strips falsy optional fields from the payload to avoid sending empty strings or undefined values
type TOptionalIntegrationFields = Omit<
  IConnectIntegrationPayload,
  "redirectTo" | "provider"
>;

// NOTE: Split out of buildOptionalFields to keep it under the cognitive-complexity
// threshold. Every `&&` in that flat list counts toward the score, and adding the
// two Nextcloud credentials took it from 14 to 16 (sonar S3776). Same idiom and
// same behaviour: a falsy value is omitted rather than sent as an empty string.
const buildNextcloudFields = ({
  nextcloudLoginName,
  nextcloudAppPassword,
}: Pick<
  TOptionalIntegrationFields,
  "nextcloudLoginName" | "nextcloudAppPassword"
>): Partial<TOptionalIntegrationFields> => ({
  ...(nextcloudLoginName && { nextcloudLoginName }),
  ...(nextcloudAppPassword && { nextcloudAppPassword }),
});

const buildOptionalFields = ({
  ewsUrl,
  ewsUsername,
  ewsPassword,
  ewsDomain,
  apiKey,
  clientId,
  clientSecret,
  crowdstrikeRegion,
  mcpUuid,
  mcpTitle,
  mcpUrl,
  mcpToken,
  mcpTransport,
  mcpAuthMode,
  mcpAuthHeaderName,
  bamboohrCompanyDomain,
  nextcloudLoginName,
  nextcloudAppPassword,
  connectorType,
}: TOptionalIntegrationFields): Partial<TOptionalIntegrationFields> => ({
  ...(ewsUrl && { ewsUrl }),
  ...(ewsUsername && { ewsUsername }),
  ...(ewsPassword && { ewsPassword }),
  ...(ewsDomain && { ewsDomain }),
  ...(apiKey && { apiKey }),
  ...(clientId && { clientId }),
  ...(clientSecret && { clientSecret }),
  ...(crowdstrikeRegion && { crowdstrikeRegion }),
  ...(mcpUuid && { mcpUuid }),
  ...(mcpTitle && { mcpTitle }),
  ...(mcpUrl && { mcpUrl }),
  ...(mcpToken && { mcpToken }),
  // NOTE: NSAI-5354 — transport and auth scheme for an MCP server. Both are
  // falsy-stripped like every other optional field, so an older caller that
  // omits them still gets the server's legacy defaults.
  ...(mcpTransport && { mcpTransport }),
  ...(mcpAuthMode && { mcpAuthMode }),
  ...(mcpAuthHeaderName && { mcpAuthHeaderName }),
  ...(bamboohrCompanyDomain && { bamboohrCompanyDomain }),
  ...buildNextcloudFields({ nextcloudLoginName, nextcloudAppPassword }),
  ...(connectorType && { connectorType }),
});

export const useConnectIntegration = () => {
  const queryClient = useQueryClient();
  const { capture } = useProductAnalytics();

  return useMutation({
    mutationFn: async ({
      provider,
      isExternalIntegrationsProviderEnabled,
      ...optionalFields
    }: Omit<IConnectIntegrationPayload, "redirectTo">) => {
      const response = await integrationApi.connect({
        redirectTo: globalThis.location.href,
        provider,
        ...buildOptionalFields(optionalFields),
      });

      // NOTE: An aggregator connect resolves to the aggregator's HOSTED connect
      // URL, which must be navigated to — not a server-side credential result with
      // success/error/message params — so it can never take the parse branch below.
      // Without this a provider that is BOTH in
      // DIRECT_INTEGRATION_CONNECTION_PROVIDERS and has an aggregator rail
      // (Fireflies via Pipedream) resolved successfully without navigating, leaving
      // the card stuck on "Connecting": connectingProviders is only cleared
      // onError (NSAI-3765).
      const isAggregatorConnect =
        optionalFields.connectorType === EConnectorType.PIPEDREAM ||
        optionalFields.connectorType === EConnectorType.COMPOSIO;

      if (
        // NOTE: Only direct integration connection providers only for app integrations
        // not for composio integrations
        !isAggregatorConnect &&
        DIRECT_INTEGRATION_CONNECTION_PROVIDERS.includes(
          provider as EIntegrationProvider,
        ) &&
        !isExternalIntegrationsProviderEnabled
      ) {
        // NOTE: NSAI-6112 — resolved through the shared allowlist like every
        // other callback URL, so the toast wording is always the client's own.
        const outcome = parseCallbackOutcome({
          query: Object.fromEntries(new URL(response.url).searchParams),
        });

        if (outcome) {
          const text = i18n.t(outcome.messageKey);

          if (!outcome.isSuccess) {
            toast.error(text);
          }

          if (outcome.isSuccess) {
            // NOTE: NSAI-4616 — only the DIRECT rail can be counted here: an
            // aggregator connect (Pipedream / Composio) navigates away and
            // finishes on a redirect back, where nothing distinguishes it from
            // any other `?success=true` landing. Counting those from the
            // generic param handler would invent connections that never
            // happened, so they are a documented gap rather than a wrong
            // number — see the tracking plan.
            capture({
              event: EAnalyticsEvent.INTEGRATION_CONNECTED,
              properties: { provider },
            });
            toast.success(text);
          }
        }

        // NOTE: NSAI-5354 — MCP also invalidates its approval-target query; see
        // getConnectionChangeQueryKeys for why one key was not enough.
        for (const queryKey of getConnectionChangeQueryKeys(provider)) {
          queryClient.invalidateQueries({ queryKey });
        }

        return;
      }

      globalThis.location.href = response.url;
    },
  });
};
